import type { ChecklistItemDto, LabelDto, SectionDto, TaskDto } from '@werft/contracts';
import { notifySaved } from '../../platform/saved.js';
import { keyBetween, LABEL_COLORS, type ColorValue, type LabelColor } from '@werft/domain';
import { t } from '../../i18n/index.js';
import { ApiError, newOpId } from '../../platform/api.js';
import { keys, queryClient } from '../../platform/queries.js';
import { removeChecklistItem, removeLabel, removeSection, upsertChecklistItem, upsertLabel, upsertSection, upsertTask } from '../../platform/realtime.js';
import { outbox } from '../../platform/sync.js';
import { toast } from '../../platform/toast.js';

/**
 * Schreibwege von Paket P8 „Aufgaben vertiefen“: Abschnitte, Labels, Checklisten. Alles über den Ausgangskorb
 * (ADR 0007). Checklisten-Punkte und Label-Zuordnungen erscheinen sofort (Overlay); Abschnitte und Labels selbst
 * nach Bestätigung (Struktur, selten geändert – wie Board-Spalten).
 */

const failed = (err: unknown) => {
  if (err instanceof ApiError && err.status === 404) return; // schon weg – Ziel erreicht
  toast(t('sync.failed', { message: err instanceof Error ? err.message : t('task.failed') }), { kind: 'error' });
};

/** Strukturänderung mit Versionsprüfung: die Absicht gilt weiter, auf den neuen Stand gesetzt (höchstens dreimal) */
function rebase<T extends { version: number }>(upsert: (pid: string, x: T) => void) {
  return {
    done: (e: { projectId: string }, r: unknown) => upsert(e.projectId, r as T),
    fail: (e: { projectId: string; body: unknown; meta?: Record<string, unknown> }, err: unknown) => {
      const current = err instanceof ApiError && err.code === 'version_conflict' ? (err.body.current as T) : null;
      const n = (e.meta?.rebases as number | undefined) ?? 0;
      if (current && n < 3) return { opId: newOpId(), body: { ...(e.body as object), expectedVersion: current.version }, meta: { rebases: n + 1 } };
      if (current) upsert(e.projectId, current);
      failed(err);
    },
  };
}

/* ---------- Abschnitte ---------- */

outbox.register('section.create', { done: (e, r) => upsertSection(e.projectId, r as SectionDto), fail: (_e, err) => failed(err) });
outbox.register('section.update', rebase<SectionDto>(upsertSection));
outbox.register('section.remove', {
  done: (e) => removeSection(e.projectId, (e.meta as { id: string }).id),
  fail: (_e, err) => failed(err),
});

export function createSection(projectId: string, listId: string, name: string): Promise<SectionDto> {
  const opId = newOpId();
  return outbox.enqueue<SectionDto>({ opId, kind: 'section.create', projectId, method: 'POST', url: `/api/projects/${projectId}/sections`, body: { opId, listId, name } });
}

export function renameSection(projectId: string, s: SectionDto, name: string) {
  const opId = newOpId();
  // sofort sichtbar; die Antwort bringt die neue Version
  queryClient.setQueryData(keys.snapshot(projectId), (old: { sections: SectionDto[] } | undefined) =>
    old ? { ...old, sections: old.sections.map((x) => (x.id === s.id ? { ...x, name } : x)) } : old,
  );
  void outbox
    .enqueue({ opId, kind: 'section.update', projectId, method: 'PATCH', url: `/api/sections/${s.id}`, body: { opId, expectedVersion: s.version, patch: { name } } })
    .catch(() => {});
}

/** Abschnitt an einen Index unter seinen Geschwistern verschieben (Menü „nach oben/unten“) */
export function moveSection(projectId: string, s: SectionDto, siblings: SectionDto[], to: number) {
  const others = siblings.filter((x) => x.id !== s.id);
  const before = others[to] ?? null;
  const after = to > 0 ? (others[to - 1] ?? null) : null;
  let position = s.position;
  try {
    position = keyBetween(after?.position ?? null, before?.position ?? null);
  } catch {
    /* Kollision: der Server rechnet neu */
  }
  queryClient.setQueryData(keys.snapshot(projectId), (old: { sections: SectionDto[] } | undefined) =>
    old ? { ...old, sections: old.sections.map((x) => (x.id === s.id ? { ...x, position } : x)).sort((a, b) => (a.position < b.position ? -1 : 1)) } : old,
  );
  const opId = newOpId();
  void outbox
    .enqueue({
      opId,
      kind: 'section.update',
      projectId,
      method: 'POST',
      url: `/api/sections/${s.id}/move`,
      body: { opId, expectedVersion: s.version, ...(after ? { afterId: after.id } : before ? { beforeId: before.id } : {}) },
    })
    .catch(() => {});
}

/** Auflösen (Rückweg): die Aufgaben bleiben in der Liste, nur ohne Abschnitt */
export function dissolveSection(projectId: string, s: SectionDto) {
  const opId = newOpId();
  removeSection(projectId, s.id);
  void outbox.enqueue({ opId, kind: 'section.remove', projectId, method: 'DELETE', url: `/api/sections/${s.id}`, body: { opId }, meta: { id: s.id } }).catch(() => {});
  toast(t('section.dissolved', { name: s.name }));
}

/* ---------- Labels ---------- */

outbox.register('label.create', { done: (e, r) => upsertLabel(e.projectId, r as LabelDto), fail: (_e, err) => failed(err) });
outbox.register('label.update', rebase<LabelDto>(upsertLabel));
outbox.register('label.remove', { done: (e) => removeLabel(e.projectId, (e.meta as { id: string }).id), fail: (_e, err) => failed(err) });
outbox.register('task.labels', { done: (e, r) => upsertTask(e.projectId, r as TaskDto), fail: (_e, err) => failed(err) });

/** Nächste freie Farbe: neue Labels unterscheiden sich, ohne dass jemand eine Farbe wählen muss */
export function nextLabelColor(labels: LabelDto[]): LabelColor {
  const used = new Map<string, number>();
  for (const l of labels) used.set(l.color, (used.get(l.color) ?? 0) + 1);
  return [...LABEL_COLORS].sort((a, b) => (used.get(a) ?? 0) - (used.get(b) ?? 0))[0]!;
}

export function createLabel(projectId: string, name: string, color: ColorValue): Promise<LabelDto> {
  const opId = newOpId();
  return outbox.enqueue<LabelDto>({ opId, kind: 'label.create', projectId, method: 'POST', url: `/api/projects/${projectId}/labels`, body: { opId, name, color } });
}

export function updateLabel(projectId: string, l: LabelDto, patch: { name?: string; color?: ColorValue }) {
  const opId = newOpId();
  queryClient.setQueryData(keys.snapshot(projectId), (old: { labels: LabelDto[] } | undefined) =>
    old ? { ...old, labels: old.labels.map((x) => (x.id === l.id ? { ...x, ...patch } : x)) } : old,
  );
  void outbox.enqueue({ opId, kind: 'label.update', projectId, method: 'PATCH', url: `/api/labels/${l.id}`, body: { opId, expectedVersion: l.version, patch } }).catch(() => {});
}

export function deleteLabel(projectId: string, l: LabelDto) {
  const opId = newOpId();
  removeLabel(projectId, l.id);
  void outbox.enqueue({ opId, kind: 'label.remove', projectId, method: 'DELETE', url: `/api/labels/${l.id}`, body: { opId }, meta: { id: l.id } }).catch(() => {});
}

/** Labels an einer Aufgabe als Delta (wie Zuständige) */
export function changeLabels(task: TaskDto, add: string[], remove: string[]): Promise<unknown> {
  const opId = newOpId();
  const labelIds = [...task.labelIds.filter((x) => !remove.includes(x)), ...add.filter((x) => !task.labelIds.includes(x))];
  return outbox
    .enqueue({
      opId,
      kind: 'task.labels',
      projectId: task.projectId,
      method: 'PATCH',
      url: `/api/tasks/${task.id}/labels`,
      body: { opId, add, remove },
      overlay: { type: 'patch', taskId: task.id, fields: { labelIds } },
    })
    .then(() => notifySaved(task.id, ['labelIds']))
    .catch(() => {});
}

/** „Label ‚X‘ anlegen“ direkt beim Zuweisen – kein Umweg über eine Verwaltung */
export async function createAndAssignLabel(task: TaskDto, name: string, labels: LabelDto[]) {
  const label = await createLabel(task.projectId, name, nextLabelColor(labels));
  await changeLabels(task, [label.id], []);
}

/* ---------- Checkliste ---------- */

const checkFailed = (e: { projectId: string; meta?: Record<string, unknown> }, err: unknown) => {
  const m = e.meta as { taskId?: string } | undefined;
  if (m?.taskId) void queryClient.invalidateQueries({ queryKey: keys.checklist(e.projectId, m.taskId) });
  failed(err);
};

outbox.register('checklist.create', { done: (e, r) => upsertChecklistItem(e.projectId, r as ChecklistItemDto), fail: checkFailed });
outbox.register('checklist.update', {
  done: (e, r) => upsertChecklistItem(e.projectId, r as ChecklistItemDto),
  fail: (e, err) => {
    // Text gleichzeitig geändert: den Stand der anderen Person zeigen, die eigene Fassung bleibt im Hinweis
    if (err instanceof ApiError && err.code === 'version_conflict') {
      const current = err.body.current as ChecklistItemDto;
      upsertChecklistItem(e.projectId, current);
      toast(t('checklist.conflict', { text: current.text }), { kind: 'error' });
      return;
    }
    checkFailed(e, err);
  },
});
outbox.register('checklist.move', { done: (e, r) => upsertChecklistItem(e.projectId, r as ChecklistItemDto), fail: checkFailed });
outbox.register('checklist.remove', {
  done: (e) => {
    const m = e.meta as { taskId: string; id: string };
    removeChecklistItem(e.projectId, m.taskId, m.id);
  },
  fail: checkFailed,
});
outbox.register('checklist.promote', {
  done: (e, r) => {
    const m = e.meta as { taskId: string; id: string };
    removeChecklistItem(e.projectId, m.taskId, m.id);
    upsertTask(e.projectId, r as TaskDto);
  },
  fail: checkFailed,
});

export function addChecklistItem(task: TaskDto, text: string, items: ChecklistItemDto[]): void {
  const opId = newOpId();
  const item: ChecklistItemDto = { id: `tmp-${opId}`, taskId: task.id, text, done: false, position: `￿${Date.now().toString(36)}`, version: 0 };
  void items;
  void outbox
    .enqueue({
      opId,
      kind: 'checklist.create',
      projectId: task.projectId,
      method: 'POST',
      url: `/api/tasks/${task.id}/checklist`,
      body: { opId, text },
      overlay: { type: 'check', item },
      meta: { taskId: task.id },
    })
    .catch(() => {});
}

export function updateChecklistItem(projectId: string, item: ChecklistItemDto, patch: { text?: string; done?: boolean }) {
  const opId = newOpId();
  void outbox
    .enqueue({
      opId,
      kind: 'checklist.update',
      projectId,
      method: 'PATCH',
      url: `/api/checklist/${item.id}`,
      body: { opId, expectedVersion: item.version, patch },
      overlay: { type: 'checkPatch', taskId: item.taskId, itemId: item.id, fields: patch },
      meta: { taskId: item.taskId },
    })
    .catch(() => {});
}

export function moveChecklistItem(projectId: string, item: ChecklistItemDto, items: ChecklistItemDto[], to: number) {
  const others = items.filter((x) => x.id !== item.id);
  const before = others[to] ?? null;
  const after = to > 0 ? (others[to - 1] ?? null) : null;
  let position = item.position;
  try {
    position = keyBetween(after?.position ?? null, before?.position ?? null);
  } catch {
    /* Kollision: der Server rechnet neu */
  }
  const opId = newOpId();
  void outbox
    .enqueue({
      opId,
      kind: 'checklist.move',
      projectId,
      method: 'POST',
      url: `/api/checklist/${item.id}/move`,
      body: { opId, ...(after ? { afterId: after.id } : before ? { beforeId: before.id } : {}) },
      overlay: { type: 'checkPatch', taskId: item.taskId, itemId: item.id, fields: { position } },
      meta: { taskId: item.taskId },
    })
    .catch(() => {});
}

export function removeChecklistEntry(projectId: string, item: ChecklistItemDto) {
  const opId = newOpId();
  void outbox
    .enqueue({
      opId,
      kind: 'checklist.remove',
      projectId,
      method: 'DELETE',
      url: `/api/checklist/${item.id}`,
      body: { opId },
      overlay: { type: 'checkRemove', taskId: item.taskId, itemId: item.id },
      meta: { taskId: item.taskId, id: item.id },
    })
    .catch(() => {});
}

/** Punkt zur Unteraufgabe aufwerten: verschwindet sofort aus der Checkliste, erscheint als Unteraufgabe */
export function promoteChecklistItem(projectId: string, item: ChecklistItemDto) {
  const opId = newOpId();
  void outbox
    .enqueue({
      opId,
      kind: 'checklist.promote',
      projectId,
      method: 'POST',
      url: `/api/tasks/${item.taskId}/checklist/${item.id}/promote`,
      body: { opId },
      overlay: { type: 'checkRemove', taskId: item.taskId, itemId: item.id },
      meta: { taskId: item.taskId, id: item.id },
    })
    .catch(() => {});
  toast(t('checklist.promoted', { text: item.text }));
}
